import Box from 'Components/Atoms/Box';
import { ArrowDropDown } from 'Components/Atoms/Icons';
import LockedProductIcon from 'Components/Atoms/Icons/LockedProduct';
import MenuItem from 'Components/Atoms/MenuItem';
import Popover from 'Components/Atoms/Popover';
import Typography from 'Components/Atoms/Typography';
import Accordion from 'Components/Molecules/Accordion';
import usePinAuth from 'CustomHooks/usePinAuth';
import React, { useState, useEffect, useMemo } from 'react';
import { ReservationShift } from 'types/shifts';

type OccassionDropdownProps = {
  isHidden: boolean;
  occassions: { id: string; title: string; hidden?: boolean }[];
  value: string | undefined;
  onChange: (newValue: string | number) => void;
  style?: React.CSSProperties;
  /**
   * Date prop kept for API symmetry with the parent (which passes
   * `reservation.date`). The shifts list is already filtered server-side
   * to that date in `ReservationContext`, so we no longer need to
   * re-check per-shift here.
   */
  date?: string;
  shifts?: ReservationShift[] | null;
};

const OccassionDropdown = ({
  isHidden,
  occassions,
  value,
  onChange,
  style,
  shifts,
}: OccassionDropdownProps) => {
  const [anchorEl, setanchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);

  const { checkAuthentication, authenticate } = usePinAuth();

  useEffect(() => {
    let noneHiddenOccassion = occassions?.find((o) => !o.hidden)?.id;

    if (isHidden && occassions.length) {
      onChange(noneHiddenOccassion || occassions[0].id);
    } else if (
      noneHiddenOccassion &&
      occassions.filter((o) => !o.hidden).length === 1
    ) {
      onChange(noneHiddenOccassion);
    }
  }, [isHidden, occassions]);

  // Filter occasions based on search term
  const filteredOccassions = occassions.filter((occasion) =>
    occasion.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset search when popover closes
  useEffect(() => {
    if (!anchorEl) {
      setSearchTerm('');
      setMoreOpen(false);
    }
  }, [anchorEl]);

  const showAdvanced = occassions.length > 3;

  // Compute set of occasion IDs that have a shift on the selected date.
  // ReservationContext.shifts is already pre-filtered server-side to the
  // current calendar date (see useFloorPlan -> /v03/shifts/{rid}/{date}),
  // so we just collect the union of `shift.occassions`.
  const occassionIdsWithShiftToday = useMemo<Set<string> | null>(() => {
    if (!showAdvanced || !shifts || !shifts.length) return null;
    const ids = new Set<string>();
    for (const s of shifts) {
      if (Array.isArray(s.occassions)) {
        for (const oId of s.occassions) ids.add(oId);
      }
    }
    return ids;
  }, [showAdvanced, shifts]);

  const sortedFiltered = filteredOccassions.slice().sort(
    (a, b) =>
      (b.hidden ? 1 : 0) - (a.hidden ? 1 : 0) ||
      a.title.localeCompare(b.title)
  );

  const renderItem = (o: { id: string; title: string; hidden?: boolean }) => (
    <MenuItem
      value={o.id}
      selected={o.id === value}
      onClick={(e, nV) =>
        authenticate(
          o.id as any,
          () => {
            onChange(nV);
            setanchorEl(null);
          },
          undefined,
          { action: 'override.occassion', category: 'override' }
        )
      }
      key={o.id}
      iconRight={
        checkAuthentication(o.id as any) ? (
          <LockedProductIcon
            width="20"
            height="20"
            noStroke
            style={{ color: 'var(--color-secondary)' }}
          />
        ) : undefined
      }
    >
      {o.title}
    </MenuItem>
  );

  // Decide whether to split into "today" + "more" accordion.
  // Only split when: showAdvanced + we have non-empty shift coverage +
  // no active search. If the set is empty (closed day, etc.) we fall
  // back to showing every occasion flat.
  const splitByShifts =
    showAdvanced &&
    occassionIdsWithShiftToday !== null &&
    occassionIdsWithShiftToday.size > 0 &&
    searchTerm.trim() === '';

  const todayItems = splitByShifts
    ? sortedFiltered.filter((o) =>
        occassionIdsWithShiftToday!.has(o.id)
      )
    : sortedFiltered;

  const otherItems = splitByShifts
    ? sortedFiltered.filter(
        (o) => !occassionIdsWithShiftToday!.has(o.id)
      )
    : [];

  if (isHidden && occassions.length) return <></>;

  return (
    <Box>
      <Typography
        block
        variant="text-3"
        translation="reservations"
        style={{ marginBottom: 8 }}
      >
        Occassion
      </Typography>
      <Box
        outline
        background
        flex
        padding="sm"
        onClick={(e) => setanchorEl((x) => (!x ? e.currentTarget : null))}
        className="space-between clickable"
        style={style}
      >
        <Typography variant="text-3" color="subdued" block>
          {occassions.find((o) => o.id === value)?.title || value}
        </Typography>
        <ArrowDropDown style={{ fill: 'var(--color-text-subdued)' }} />
      </Box>
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setanchorEl(null)}
        placement="bottom"
        padding={0}
      >
        <Box style={{ maxHeight: 360, overflowY: 'auto', minWidth: 240 }}>
          {/* Search bar - only show if more than 3 occasions */}
          {showAdvanced && (
            <Box
              padding="sm"
              style={{ borderBottom: '1px solid var(--color-border)' }}
            >
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text)',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          )}

          {todayItems.map(renderItem)}

          {splitByShifts && otherItems.length > 0 && (
            <Box style={{ marginTop: 4 }}>
              <Box
                padding="sm"
                onClick={() => setMoreOpen((x) => !x)}
                className="clickable"
                style={{
                  borderTop: '1px solid var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
              >
                <Typography variant="text-3" color="subdued">
                  {`Weitere Occassions (${otherItems.length})`}
                </Typography>
                <ArrowDropDown
                  style={{
                    fill: 'var(--color-text-subdued)',
                    transform: moreOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms',
                  }}
                />
              </Box>
              <Accordion open={moreOpen}>
                <Box>{otherItems.map(renderItem)}</Box>
              </Accordion>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default OccassionDropdown;
